import React from 'react';
import './Button.css';

/**
 * Shared Button Component
 * Single source of truth for the app's button styling. Replaces the many
 * ad-hoc per-page button styles with five consistent variants.
 *
 * Variants:
 *  - primary    : main call-to-action (brand gradient + white text)
 *  - secondary  : alternative action, incl. Cancel/dismiss (white fill, blue outline + blue text)
 *  - danger     : destructive action (red fill + white text)
 *  - link       : inline / text-only action (no fill, blue text)
 *
 * Accessibility:
 *  - Renders a native <button>, so it is focusable and keyboard-operable.
 *  - Disabled buttons set the real `disabled` attribute (exposed to assistive
 *    tech) and dim to 40% — the app-wide standard for inactive controls.
 *  - Visible focus ring via :focus-visible.
 *
 * @param {('primary'|'secondary'|'danger'|'link')} variant - Visual style (default 'primary')
 * @param {('button'|'submit'|'reset')} type - Native button type (default 'button')
 * @param {boolean} disabled  - Disabled state (default false)
 * @param {boolean} fullWidth - Stretch to the container width (default false)
 * @param {string}  className - Extra classes for layout overrides (optional)
 * @param {React.ReactNode} children - Button label / content
 */
const Button = ({
  variant = 'primary',
  type = 'button',
  disabled = false,
  fullWidth = false,
  className = '',
  children,
  ...rest
}) => {
  const classes = [
    'app-btn',
    `app-btn--${variant}`,
    fullWidth ? 'app-btn--full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} disabled={disabled} {...rest}>
      {children}
    </button>
  );
};

export default Button;
